import {
  Collapse,
  Popover,
  PopoverInteractionKind,
  Position,
} from "@blueprintjs/core"
import { Link } from "@src/Components/Link"
import { Section } from "@src/Components/StyledUtils"
import {
  DateFormat,
  useUserLocaleDateFormatting,
} from "@src/Hooks/useUserLocaleDateFormatting"
import { tState } from "@src/Model/Model"
import { colors } from "@src/theme"
import * as T from "@src/types"
import { FaIcon } from "@zeguro/component-library"
import { Button } from "@zeguro/component-library"
import { differenceInMinutes } from "date-fns"
import * as React from "react"
import { useSelector } from "react-redux"

import { Box, DateHolder, PageBreak, SubHeading } from "../styles"
import {
  BoxBody,
  CollapseArrow,
  DownloadHeadWrapper,
  DownloadRow,
  Issue,
  IssueContainer,
  IssueCount,
  IssueSeverity,
  IssueSeverityContainer,
  IssuesText,
  Oval,
  PopoverContent,
  PopoverDownloadContainer,
  ScanInfoContainer,
  ScanInfoContent,
  ScanInfoElement,
  ScanInfoElementText,
  SuperScript,
} from "./styles"

interface IScanReportCard {
  scan: T.IProbelyScan
  clientWidth: number
  targetName: string
  getScanReport: (
    targetName: string,
    targetId: string,
    scanId: string,
    reportType: string,
  ) => void
}

const formatSeverityCount = (count: number) => {
  if (count > 999) {
    return (
      <>
        999<SuperScript>+</SuperScript>
      </>
    )
  }
  return count
}

export const ScanReportCard = ({
  scan,
  getScanReport,
  clientWidth,
  targetName,
}: IScanReportCard) => {
  const [showMore, setShowMore] = React.useState(false)
  const { helpCenter } = useSelector((state: tState) => state.brand)
  const { formatDate } = useUserLocaleDateFormatting()

  return React.useMemo(() => {
    const startTime = formatDate(new Date(scan.startTime), DateFormat.Time)
    const completeTime = formatDate(
      new Date(scan.completeTime),
      DateFormat.Time,
    )

    const ranTime = differenceInMinutes(
      new Date(scan.completeTime),
      new Date(scan.startTime),
    )

    if (scan.status === "canceled" || scan.status === "failed") {
      return (
        <Section flex="center normal column" key={scan.scanId}>
          <Box
            hasBorder
            borderColor={
              scan.status === "canceled" ? colors.pink : colors.error
            }
          >
            <BoxBody>
              <SubHeading>
                <DateHolder>
                  {formatDate(new Date(scan.completeTime), DateFormat.Date)}
                </DateHolder>
              </SubHeading>
              <ScanInfoContent>
                <ScanInfoElement>
                  <FaIcon
                    fontSize="24px"
                    iconType="fas"
                    iconName="bolt"
                    color={colors.unitedNationsBlue}
                  />
                  <ScanInfoElementText>
                    {scan.scanProfile} Scan
                  </ScanInfoElementText>
                </ScanInfoElement>
                <ScanInfoElement>
                  <FaIcon
                    fontSize="24px"
                    iconType="fas"
                    iconName="arrow-from-left"
                    color={colors.unitedNationsBlue}
                  />
                  <ScanInfoElementText>
                    Started <strong>{startTime}</strong>
                  </ScanInfoElementText>
                </ScanInfoElement>
                <ScanInfoElement>
                  <FaIcon
                    fontSize="24px"
                    iconType="fas"
                    iconName="stopwatch"
                    color={colors.unitedNationsBlue}
                  />
                  <ScanInfoElementText>
                    Ran{" "}
                    <strong>
                      {ranTime < 2 ? `${ranTime} minute` : `${ranTime} minutes`}
                    </strong>
                  </ScanInfoElementText>
                </ScanInfoElement>
                <ScanInfoElement>
                  {scan.status === "canceled" ? (
                    <>
                      <FaIcon
                        fontSize="24px"
                        iconType="fas"
                        iconName="hand-paper"
                        color={colors.pink}
                      />
                      <ScanInfoElementText>
                        Manually canceled <strong>{completeTime}</strong>
                      </ScanInfoElementText>
                    </>
                  ) : (
                    <>
                      <FaIcon
                        fontSize="24px"
                        iconType="fas"
                        iconName="frown"
                        color={colors.error}
                      />
                      <ScanInfoElementText color={colors.error}>
                        <FaIcon
                          iconType="fas"
                          iconName="exclamation-triangle"
                          fontSize="16px"
                          color={colors.error}
                        />{" "}
                        Failed <strong>{completeTime}</strong>
                      </ScanInfoElementText>
                    </>
                  )}
                </ScanInfoElement>
              </ScanInfoContent>
            </BoxBody>
          </Box>
          <PageBreak />
        </Section>
      )
    } else {
      return (
        <Section flex="center normal column" key={scan.scanId}>
          <Box hasBorder>
            <BoxBody>
              <DownloadHeadWrapper>
                <SubHeading>
                  <DateHolder>
                    {formatDate(new Date(scan.completeTime), DateFormat.Date)}
                  </DateHolder>
                </SubHeading>
                {clientWidth > 624 && (
                  <DownloadRow>
                    <Popover
                      content={
                        <PopoverDownloadContainer>
                          <Button
                            variant="tertiary"
                            onClick={() => {
                              getScanReport(
                                targetName,
                                scan.targetId,
                                scan.scanId,
                                "owasp",
                              )
                            }}
                          >
                            Standard Version (OWASP)
                          </Button>
                          <Button
                            variant="tertiary"
                            onClick={() => {
                              getScanReport(
                                targetName,
                                scan.targetId,
                                scan.scanId,
                                "pci",
                              )
                            }}
                          >
                            PCI Version
                          </Button>
                        </PopoverDownloadContainer>
                      }
                      interactionKind={PopoverInteractionKind.CLICK}
                      minimal
                      position={Position.BOTTOM_LEFT}
                    >
                      <Button
                        variant="secondary"
                        width="187px"
                        id="scan-tables-download-report-btn"
                      >
                        <FaIcon
                          iconType="fas"
                          iconName="file-pdf"
                          fontSize="16px"
                        />
                        Download Report
                      </Button>
                    </Popover>
                    &nbsp;&nbsp;
                    <Popover
                      popoverClassName="bp5-dark"
                      content={
                        <PopoverContent>
                          <span
                            style={{
                              fontSize: "18px",
                              lineHeight: "24px",
                              fontWeight: "bold",
                            }}
                          >
                            Which report is best for me?
                          </span>
                          <br /> <br />
                          Use <b>Standard</b> for compliance requirements other
                          than PCI (e.g. SOC2 and HIPAA).
                          <br /> <br />
                          Use <b>PCI</b> if your business transmits, processes,
                          or stores credit card information.
                          <br /> <br />
                          <Link
                            href={`https://${helpCenter}/hc/en-us/articles/360038743493`}
                            id="learn-more-scan-types"
                            margin="0px"
                            isUnderline
                            color={colors.white}
                            withIcon
                          >
                            Learn More
                          </Link>
                        </PopoverContent>
                      }
                      interactionKind={PopoverInteractionKind.HOVER}
                      position={Position.TOP}
                    >
                      <FaIcon
                        iconType="fas"
                        iconName="question-circle"
                        fontSize="16px"
                        color={colors.gray40}
                        cursor="pointer"
                      />
                    </Popover>
                  </DownloadRow>
                )}
              </DownloadHeadWrapper>
              <IssueContainer>
                <IssuesText>
                  {(
                    (scan.criticals || 0) +
                    scan.highs +
                    scan.mediums +
                    scan.lows
                  ).toLocaleString()}{" "}
                  Issues
                </IssuesText>
                <IssueSeverityContainer>
                  <Issue opacity={(scan.criticals || 0) > 0 ? 1 : 0.5}>
                    <IssueCount>
                      {formatSeverityCount(scan.criticals || 0)}
                    </IssueCount>
                    <IssueSeverity>
                      <Oval color={colors.error} />
                      Critical
                    </IssueSeverity>
                  </Issue>
                  <Issue opacity={scan.highs > 0 ? 1 : 0.5}>
                    <IssueCount>{formatSeverityCount(scan.highs)}</IssueCount>
                    <IssueSeverity>
                      <Oval color={colors.red} />
                      High
                    </IssueSeverity>
                  </Issue>
                  <Issue opacity={scan.mediums > 0 ? 1 : 0.5}>
                    <IssueCount>{formatSeverityCount(scan.mediums)}</IssueCount>
                    <IssueSeverity>
                      <Oval color={colors.orange90} />
                      Medium
                    </IssueSeverity>
                  </Issue>
                  <Issue opacity={scan.lows > 0 ? 1 : 0.5}>
                    <IssueCount>{formatSeverityCount(scan.lows)}</IssueCount>
                    <IssueSeverity>
                      <Oval color={colors.warning} />
                      Low
                    </IssueSeverity>
                  </Issue>
                </IssueSeverityContainer>
              </IssueContainer>
              {clientWidth < 625 && (
                <DownloadRow justifyContent="center">
                  <Popover
                    content={
                      <PopoverDownloadContainer>
                        <Button
                          variant="tertiary"
                          onClick={() => {
                            getScanReport(
                              targetName,
                              scan.targetId,
                              scan.scanId,
                              "owasp",
                            )
                          }}
                        >
                          Standard Version (OWASP)
                        </Button>
                        <Button
                          variant="tertiary"
                          onClick={() => {
                            getScanReport(
                              targetName,
                              scan.targetId,
                              scan.scanId,
                              "pci",
                            )
                          }}
                        >
                          PCI Version
                        </Button>
                      </PopoverDownloadContainer>
                    }
                    interactionKind={PopoverInteractionKind.CLICK}
                    minimal
                    position={Position.BOTTOM_LEFT}
                  >
                    <Button
                      variant="secondary"
                      width="187px"
                      id="scan-tables-download-report-btn"
                    >
                      Download Report
                    </Button>
                  </Popover>
                </DownloadRow>
              )}
              <ScanInfoContainer>
                <Button
                  variant="tertiary"
                  onClick={() => setShowMore(!showMore)}
                  color={colors.darkBlue}
                >
                  {showMore ? "Hide details" : "View details"}{" "}
                  <CollapseArrow
                    iconType="fas"
                    iconName="caret-down"
                    showMore={showMore}
                  />
                </Button>
                <Collapse isOpen={showMore}>
                  <ScanInfoContent>
                    <ScanInfoElement>
                      <FaIcon
                        fontSize="24px"
                        iconType="fas"
                        iconName="bolt"
                        color={colors.unitedNationsBlue}
                      />
                      <ScanInfoElementText>
                        {scan.scanProfile} Scan
                      </ScanInfoElementText>
                    </ScanInfoElement>
                    <ScanInfoElement>
                      <FaIcon
                        fontSize="24px"
                        iconType="fas"
                        iconName="arrow-from-left"
                        color={colors.unitedNationsBlue}
                      />
                      <ScanInfoElementText>
                        Started <strong>{startTime}</strong>
                      </ScanInfoElementText>
                    </ScanInfoElement>
                    <ScanInfoElement>
                      <FaIcon
                        fontSize="24px"
                        iconType="fas"
                        iconName="stopwatch"
                        color={colors.unitedNationsBlue}
                      />
                      <ScanInfoElementText>
                        Ran{" "}
                        <strong>
                          {ranTime < 2
                            ? `${ranTime} minute`
                            : `${ranTime} minutes`}
                        </strong>
                      </ScanInfoElementText>
                    </ScanInfoElement>
                    <ScanInfoElement>
                      <FaIcon
                        fontSize="24px"
                        iconType="fas"
                        iconName="check"
                        color={colors.success}
                      />
                      <ScanInfoElementText>
                        Finished <strong>{completeTime}</strong>
                      </ScanInfoElementText>
                    </ScanInfoElement>
                  </ScanInfoContent>
                </Collapse>
              </ScanInfoContainer>
            </BoxBody>
          </Box>
          <PageBreak />
        </Section>
      )
    }
  }, [
    scan.scanId,
    scan.status,
    scan.criticals,
    scan.highs,
    scan.mediums,
    scan.lows,
    showMore,
  ])
}
